import {
	Building2,
	Calendar,
	CheckCircle2,
	ChevronDown,
	Code,
	Copy,
	Eye,
	Search,
	Share2,
	Shield,
	X,
} from "lucide-react";
import React from "react";

import { Button } from "@/app/components/ui/button";
import { cn } from "@/app/handlers/cn.utils";

import { useCompanyDashboard } from "./CompanyDashboardProvider";
import { getCompanyPublicPath } from "./paths";

export const IconButton = React.forwardRef(function IconButton({ className, children, ...props }, ref) {
	return (
		<button
			ref={ref}
			className={cn(
				"inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700",
				className,
			)}
			{...props}
		>
			{children}
		</button>
	);
});

export function VerifiedBadge({ premium = false, size = "md", variant = "default" }) {
	const sizeClasses = {
		sm: "px-2 py-1 text-xs gap-1",
		md: "px-2.5 py-1 text-sm gap-1.5",
	};

	if (variant === "careerfy") {
		return (
			<span className={cn("inline-flex items-center rounded-md bg-[#e1f0ff] font-medium text-[#1c4ed8]", sizeClasses[size])}>
				<CheckCircle2 className="h-3 w-3" />
				Verified
			</span>
		);
	}

	if (premium) {
		return (
			<span className={cn("inline-flex items-center rounded-md bg-blue-50 font-medium text-blue-700", sizeClasses[size])}>
				<Shield className="h-3.5 w-3.5" />
				Verified Employer
			</span>
		);
	}

	return (
		<span className={cn("inline-flex items-center rounded-md bg-green-50 font-medium text-green-700", sizeClasses[size])}>
			<CheckCircle2 className="h-3.5 w-3.5" />
			Verified
		</span>
	);
}

export function ShareActions({ companyId, companyName, onCopyEmbed, onShare, onPreview }) {
	const [showEmbedModal, setShowEmbedModal] = React.useState(false);
	const embedCode = `<iframe src="${window.location.origin}${getCompanyPublicPath(companyId)}" title="${companyName} jobs on Dproz" style="width:100%;min-height:720px;border:0;border-radius:16px;"></iframe>`;

	return (
		<>
			<div className="flex items-center gap-2">
				<IconButton title="Copy embed code" onClick={() => setShowEmbedModal(true)}>
					<Code className="h-4 w-4" />
				</IconButton>
				<IconButton title="Share public company page" onClick={onShare}>
					<Share2 className="h-4 w-4" />
				</IconButton>
				<IconButton title="Preview public company page" onClick={onPreview}>
					<Eye className="h-4 w-4" />
				</IconButton>
			</div>

			{showEmbedModal && (
				<div className="modal-backdrop" onClick={() => setShowEmbedModal(false)}>
					<div className="modal-content max-w-lg" onClick={(event) => event.stopPropagation()}>
						<div className="mb-3 flex items-center justify-between">
							<h3 className="text-base font-semibold text-foreground">Embed Code</h3>
							<Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowEmbedModal(false)}>
								<X className="h-4 w-4" />
							</Button>
						</div>
						<p className="mb-3 text-sm text-muted-foreground">
							Use this iframe snippet if you want to surface the public Dproz company page inside another site.
						</p>
						<pre className="mb-4 overflow-x-auto rounded-lg bg-secondary p-3 text-xs text-muted-foreground">{embedCode}</pre>
						<Button
							className="w-full"
							onClick={async () => {
								await onCopyEmbed();
								setShowEmbedModal(false);
							}}
						>
							<Copy className="mr-2 h-4 w-4" />
							Copy Embed Code
						</Button>
					</div>
				</div>
			)}
		</>
	);
}

function CompanyHeader() {
	const {
		companyId,
		company,
		companyName,
		branding,
		plan,
		planLabel,
		copyEmbedCode,
		shareCompanyPage,
		previewCompanyPage,
	} = useCompanyDashboard();
	const logoUrl = branding.companyLogo || company?.logo_url;
	const planColors = {
		starter: "bg-secondary text-foreground",
		corporate: "bg-[hsl(214,95%,93%)] text-[hsl(221,83%,53%)]",
		enterprise: "bg-gradient-brand text-white",
	};

	return (
		<div className="mb-6 flex items-center justify-between border-b border-gray-100 pb-6">
			<div className="flex items-center gap-4">
				<div className={cn("flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl shadow-md", logoUrl ? "bg-white" : "bg-gradient-to-br from-brand-500 to-brand-600")}>
					{logoUrl ? (
						<img src={logoUrl} alt={companyName} className="h-full w-full object-cover" />
					) : (
						<Building2 className="h-8 w-8 text-white" />
					)}
				</div>
				<div>
					<div className="mb-1 flex items-center gap-2">
						<h1 className="text-lg font-bold text-foreground">{companyName}</h1>
						<VerifiedBadge size="sm" variant="careerfy" />
					</div>
					<span className={cn("rounded-md px-2 py-0.5 text-xs font-medium", planColors[plan] || planColors.starter)}>
						{planLabel}
					</span>
				</div>
			</div>
			<ShareActions companyId={companyId} companyName={companyName} onCopyEmbed={copyEmbedCode} onShare={shareCompanyPage} onPreview={previewCompanyPage} />
		</div>
	);
}

export function ContentSection({ children, className = "", title, subtitle, actions = null, hideHero = false, companyHeader = false }) {
	return (
		<div className={cn("m-3 min-h-[calc(100vh-24px)] rounded-3xl border border-gray-100 bg-white p-6", className)}>
			{!hideHero && companyHeader ? <CompanyHeader /> : null}
			{!hideHero && !companyHeader && (title || subtitle || actions) && (
				<div className="mb-6 flex flex-col gap-4 border-b border-gray-100 pb-6 md:flex-row md:items-start md:justify-between">
					<div>
						{title ? <h1 className="text-2xl font-bold text-foreground">{title}</h1> : null}
						{subtitle ? <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p> : null}
					</div>
					{actions}
				</div>
			)}
			{children}
		</div>
	);
}

export function FilterBar({ searchValue, onSearchChange, searchPlaceholder = "Search...", dropdowns = [], dateRange, clearButton }) {
	return (
		<div className="flex flex-col gap-2 sm:flex-row">
			<div className="relative flex-1">
				<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
				<input
					type="text"
					value={searchValue}
					onChange={(event) => onSearchChange(event.target.value)}
					placeholder={searchPlaceholder}
					className="w-full rounded-lg border border-input bg-background py-2 pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
				/>
			</div>

			{dropdowns.map((dropdown) => (
				<div key={`${dropdown.value}-${dropdown.options.length}`} className="relative">
					<select
						value={dropdown.value}
						onChange={(event) => dropdown.onChange(event.target.value)}
						className="min-w-[140px] appearance-none rounded-lg border border-input bg-white px-3 py-2 pr-8 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
					>
						{dropdown.options.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
					<ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
				</div>
			))}

			{dateRange && (
				<div className="relative">
					<button
						type="button"
						onClick={dateRange.onTogglePicker}
						className="flex items-center gap-2 rounded-lg border border-input bg-white px-3 py-2 text-sm hover:bg-secondary/50"
					>
						<Calendar className="h-4 w-4 text-muted-foreground" />
						<span>
							{dateRange.formatDateDisplay(dateRange.startDate)} - {dateRange.formatDateDisplay(dateRange.endDate)}
						</span>
						<ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", dateRange.showPicker && "rotate-180")} />
					</button>

					{dateRange.showPicker && (
						<div className="absolute right-0 top-full z-30 mt-2 min-w-[240px] rounded-xl border border-border bg-white p-4 shadow-lg">
							<div className="space-y-3">
								<div>
									<label className="mb-1 block text-xs font-medium text-muted-foreground">Start date</label>
									<input
										type="date"
										value={dateRange.startDate}
										onChange={(event) => dateRange.onStartDateChange(event.target.value)}
										className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
									/>
								</div>
								<div>
									<label className="mb-1 block text-xs font-medium text-muted-foreground">End date</label>
									<input
										type="date"
										value={dateRange.endDate}
										onChange={(event) => dateRange.onEndDateChange(event.target.value)}
										className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
									/>
								</div>
								<Button className="w-full" onClick={dateRange.onTogglePicker}>
									Apply
								</Button>
							</div>
						</div>
					)}
				</div>
			)}

			{clearButton?.show ? (
				<button type="button" onClick={clearButton.onClick} className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground">
					{clearButton.label || "Clear filters"}
				</button>
			) : null}
		</div>
	);
}

export function ActivityCard({
	icon: Icon,
	iconBgColor,
	iconColor,
	label,
	timestamp,
	title,
	subtitle,
	actor,
	badge,
	actionLabel,
	onClick,
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="group flex w-full items-center gap-3 rounded-xl border border-border bg-white p-4 text-left transition-all hover:border-primary-200 hover:shadow-sm"
		>
			<div className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-full", iconBgColor)}>
				<Icon className={cn("h-5 w-5", iconColor)} />
			</div>
			<div className="min-w-0 flex-1">
				{label || timestamp ? (
					<div className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground">
						{label ? <span className="font-medium">{label}</span> : null}
						{label && timestamp ? <span>•</span> : null}
						{timestamp ? <span>{timestamp}</span> : null}
					</div>
				) : null}
				<p className="text-sm font-semibold text-foreground">{title}</p>
				{subtitle || badge ? (
					<div className="mt-1 flex items-center gap-2">
						{subtitle ? <p className="truncate text-xs text-muted-foreground">{subtitle}</p> : null}
						{badge ? <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", badge.className)}>{badge.text}</span> : null}
					</div>
				) : null}
				{actor ? <p className="mt-1 text-xs text-muted-foreground">{actor.name} ({actor.role})</p> : null}
			</div>
			{actionLabel ? <span className="shrink-0 text-sm font-medium text-primary-700 group-hover:text-primary-800">{actionLabel}</span> : null}
		</button>
	);
}
